import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Check,
  MapPin,
  HelpCircle,
  Phone,
  Globe,
  Calendar,
  Star,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLogSheet } from "@/contexts/LogSheetContext";
import {
  linkCuisineSlugForRestaurant,
  getPlaceExtendedDetails,
  type PlaceExtendedDetails,
} from "@/lib/google";
import { FButton } from "./FButton";
import { RestaurantPhoto } from "./RestaurantPhoto";
import { timeAgo } from "@/lib/timeAgo";

type LeafCuisine = { id: string; slug: string; name: string; emoji: string | null };

type RestaurantDetail = {
  id: string;
  name: string;
  address: string | null;
  photo_url: string | null;
  google_place_id: string | null;
  price_tier: number | null;
  visit_count: number;
  city: { name: string } | null;
  neighbourhood: { name: string } | null;
  restaurant_cuisine_types: {
    cuisine_types: { name: string; emoji: string | null } | null;
  }[];
};

type VisitorProfile = {
  user_id: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
};

type RecentVisit = {
  id: string;
  caption: string | null;
  confirmed_at: string;
  dish_ids: string[] | null;
  user: { id: string; username: string; display_name: string; avatar_url: string | null } | null;
};

export function RestaurantDetailSheet({
  restaurantId,
  open,
  onClose,
}: {
  restaurantId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const { openSheet } = useLogSheet();
  const nav = useNavigate();
  const [r, setR] = useState<RestaurantDetail | null>(null);
  const [visitors, setVisitors] = useState<VisitorProfile[]>([]);
  const [recent, setRecent] = useState<RecentVisit[]>([]);
  const [visitedToday, setVisitedToday] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [leafCuisines, setLeafCuisines] = useState<LeafCuisine[]>([]);
  const [suggesting, setSuggesting] = useState<string | null>(null);
  const [suggested, setSuggested] = useState(false);
  const [extended, setExtended] = useState<PlaceExtendedDetails | null>(null);
  const [hoursExpanded, setHoursExpanded] = useState(false);
  const [expandedReviews, setExpandedReviews] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !restaurantId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [rRes, visitorsRes, recentRes, todayRes] = await Promise.all([
        supabase
          .from("restaurants")
          .select(
            "id, name, address, photo_url, google_place_id, price_tier, visit_count, city:cities(name), neighbourhood:neighbourhoods(name), restaurant_cuisine_types(cuisine_types(name, emoji))",
          )
          .eq("id", restaurantId)
          .maybeSingle(),
        supabase
          .from("visits")
          .select(
            "user_id, profiles!visits_user_id_fkey(username, display_name, avatar_url)",
          )
          .eq("restaurant_id", restaurantId)
          .eq("is_public", true)
          .order("confirmed_at", { ascending: false })
          .limit(50),
        supabase
          .from("visits")
          .select(
            "id, caption, confirmed_at, dish_ids, user:profiles!visits_user_id_fkey(id, username, display_name, avatar_url)",
          )
          .eq("restaurant_id", restaurantId)
          .eq("is_public", true)
          .order("confirmed_at", { ascending: false })
          .limit(5),
        user
          ? supabase
              .from("visits")
              .select("id", { head: true, count: "exact" })
              .eq("restaurant_id", restaurantId)
              .eq("user_id", user.id)
              .gte("confirmed_at", new Date(new Date().setHours(0, 0, 0, 0)).toISOString())
          : Promise.resolve({ count: 0 } as any),
      ]);
      if (cancelled) return;
      setR((rRes.data as any) ?? null);
      // Dedupe visitors by user_id, keep order
      const seen = new Set<string>();
      const dedup: VisitorProfile[] = [];
      for (const row of (visitorsRes.data ?? []) as any[]) {
        if (!row.profiles || seen.has(row.user_id)) continue;
        seen.add(row.user_id);
        dedup.push({
          user_id: row.user_id,
          username: row.profiles.username,
          display_name: row.profiles.display_name,
          avatar_url: row.profiles.avatar_url,
        });
        if (dedup.length >= 8) break;
      }
      setVisitors(dedup);
      setRecent((recentRes.data as any) ?? []);
      setVisitedToday(((todayRes as any).count ?? 0) > 0);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, restaurantId, user]);

  useEffect(() => {
    setSuggested(false);
    setSuggestOpen(false);
    setExtended(null);
    setHoursExpanded(false);
    setExpandedReviews({});
  }, [restaurantId]);

  // Fetch extended Google Places details (phone, hours, reviews…) when sheet opens.
  useEffect(() => {
    if (!open || !r?.google_place_id) return;
    let cancelled = false;
    (async () => {
      const data = await getPlaceExtendedDetails(r.google_place_id!);
      if (!cancelled) setExtended(data);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, r?.google_place_id]);

  useEffect(() => {
    if (!suggestOpen || leafCuisines.length > 0) return;
    (async () => {
      const { data } = await supabase
        .from("cuisine_types")
        .select("id, slug, name, emoji")
        .not("parent_id", "is", null)
        .order("name");
      setLeafCuisines((data as LeafCuisine[]) ?? []);
    })();
  }, [suggestOpen, leafCuisines.length]);

  if (!open) return null;

  const cuisines =
    r?.restaurant_cuisine_types?.map((x) => x.cuisine_types).filter(Boolean) ?? [];
  const showSuggest = !!r && cuisines.length === 0 && !suggested;

  const handleLog = () => {
    if (!r) return;
    onClose();
    setTimeout(() => openSheet({ restaurantId: r.id }), 250);
  };

  const handleSuggest = async (c: LeafCuisine) => {
    if (!r || suggesting) return;
    setSuggesting(c.id);
    const ok = await linkCuisineSlugForRestaurant(r.id, c.slug);
    setSuggesting(null);
    if (ok) {
      setSuggested(true);
      setSuggestOpen(false);
      toast.success("Thanks for the suggestion!");
    } else {
      toast.error("Couldn't save suggestion");
    }
  };


  return (
    <div className="fixed inset-0 z-50 flex items-end">
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 animate-in fade-in duration-200"
      />
      <div
        className="relative w-full max-h-[92vh] flex flex-col animate-in slide-in-from-bottom duration-300"
        style={{
          backgroundColor: "var(--colour-surface)",
          borderTopLeftRadius: "var(--radius-lg)",
          borderTopRightRadius: "var(--radius-lg)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <div className="pt-2.5 pb-1 flex justify-center shrink-0">
          <div
            className="w-10 h-1 rounded-full"
            style={{ backgroundColor: "var(--colour-border)" }}
          />
        </div>
        <div className="flex items-center justify-end px-2 shrink-0">
          <button
            aria-label="Close"
            onClick={onClose}
            className="w-11 h-11 flex items-center justify-center"
          >
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto pb-4">
          {loading || !r ? (
            <div className="px-4 space-y-3">
              <div
                className="w-full aspect-video rounded-[var(--radius-md)] animate-pulse"
                style={{ backgroundColor: "var(--colour-surface-raised)" }}
              />
              <div
                className="h-6 w-2/3 rounded animate-pulse"
                style={{ backgroundColor: "var(--colour-surface-raised)" }}
              />
            </div>
          ) : (
            <>
              {/* Photo */}
              <div className="px-4">
                <div
                  className="w-full aspect-video rounded-[var(--radius-md)] overflow-hidden flex items-center justify-center text-[64px]"
                  style={{ backgroundColor: "var(--colour-surface-raised)" }}
                >
                  <RestaurantPhoto
                    restaurantId={r.id}
                    photoUrl={r.photo_url}
                    googlePlaceId={r.google_place_id}
                    emoji={cuisines[0]?.emoji ?? "🍽️"}
                    alt={r.name}
                  />
                </div>
              </div>

              {/* Name + meta */}
              <div className="px-4 mt-4">
                <h2 className="text-[24px] font-bold leading-tight">{r.name}</h2>
                {cuisines.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {cuisines.map((c, i) => (
                      <span
                        key={i}
                        className="px-2.5 py-1 rounded-[var(--radius-full)] text-[12px] font-medium"
                        style={{
                          backgroundColor: "var(--colour-surface-raised)",
                          color: "var(--colour-text-primary)",
                        }}
                      >
                        {c!.emoji ?? ""} {c!.name}
                      </span>
                    ))}
                  </div>
                )}
                {showSuggest && (
                  <button
                    onClick={() => setSuggestOpen(true)}
                    className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[var(--radius-full)] text-[12px] font-medium"
                    style={{
                      backgroundColor: "var(--colour-surface-raised)",
                      color: "var(--colour-text-secondary)",
                    }}
                  >
                    <HelpCircle size={12} />
                    Cuisine unknown — tap to suggest
                  </button>
                )}
                {r.address && (
                  <p className="mt-2 text-[14px] text-[var(--colour-text-secondary)]">
                    {r.address}
                  </p>
                )}
                <p className="mt-1 text-[13px] text-[var(--colour-text-secondary)] flex items-center gap-2">
                  {r.price_tier && (
                    <span>
                      {"£".repeat(r.price_tier)}
                      <span className="opacity-30">{"£".repeat(4 - r.price_tier)}</span>
                    </span>
                  )}
                  {(r.neighbourhood?.name || r.city?.name) && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin size={12} />
                      {[r.neighbourhood?.name, r.city?.name].filter(Boolean).join(", ")}
                    </span>
                  )}
                </p>
                <p className="mt-3 text-[14px]">
                  <strong>{r.visit_count}</strong>{" "}
                  <span className="text-[var(--colour-text-secondary)]">
                    {r.visit_count === 1 ? "person has" : "people have"} visited on Forkd
                  </span>
                </p>
              </div>

              {/* Who's been here */}
              {visitors.length > 0 && (
                <section className="mt-5">
                  <h3 className="px-4 text-[14px] font-semibold mb-2">Who's been here</h3>
                  <div className="flex gap-3 overflow-x-auto px-4 pb-1">
                    {visitors.map((v) => (
                      <button
                        key={v.user_id}
                        onClick={() => {
                          onClose();
                          setTimeout(() => nav(`/u/${v.username}`), 200);
                        }}
                        className="shrink-0 relative"
                        aria-label={v.display_name}
                      >
                        {v.avatar_url ? (
                          <img
                            src={v.avatar_url}
                            alt={v.display_name}
                            className="w-12 h-12 rounded-[var(--radius-full)] object-cover"
                            style={{ backgroundColor: "var(--colour-surface-raised)" }}
                          />
                        ) : (
                          <div
                            className="w-12 h-12 rounded-[var(--radius-full)] flex items-center justify-center text-[14px] font-semibold"
                            style={{
                              backgroundColor: "var(--colour-surface-raised)",
                              color: "var(--colour-text-secondary)",
                            }}
                          >
                            {v.display_name.slice(0, 1).toUpperCase()}
                          </div>
                        )}
                        {v.user_id === user?.id && (
                          <span
                            className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-[var(--radius-full)] flex items-center justify-center"
                            style={{ backgroundColor: "var(--colour-success)" }}
                          >
                            <Check size={12} color="white" strokeWidth={3} />
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {/* Recent visits */}
              {recent.length > 0 && (
                <section className="mt-5 px-4">
                  <h3 className="text-[14px] font-semibold mb-3">Recent visits</h3>
                  <ul className="space-y-3">
                    {recent.map((v) => (
                      <li key={v.id} className="flex gap-3">
                        {v.user?.avatar_url ? (
                          <img
                            src={v.user.avatar_url}
                            alt={v.user.display_name}
                            className="w-9 h-9 rounded-[var(--radius-full)] object-cover shrink-0"
                          />
                        ) : (
                          <div
                            className="w-9 h-9 rounded-[var(--radius-full)] shrink-0 flex items-center justify-center text-[12px] font-semibold"
                            style={{
                              backgroundColor: "var(--colour-surface-raised)",
                              color: "var(--colour-text-secondary)",
                            }}
                          >
                            {v.user?.display_name?.slice(0, 1)?.toUpperCase() ?? "?"}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px]">
                            <span className="font-semibold">
                              @{v.user?.username ?? "user"}
                            </span>{" "}
                            <span className="text-[var(--colour-text-secondary)]">
                              · {timeAgo(v.confirmed_at)}
                            </span>
                          </p>
                          {v.caption && (
                            <p className="text-[14px] mt-0.5">{v.caption}</p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Google Places extended details */}
              {extended && (
                <section className="mt-5 px-4 space-y-4">
                  {/* 1. Star rating */}
                  {extended.rating !== null && (
                    <p className="text-[13px] text-[var(--colour-text-secondary)] flex items-center gap-1">
                      <span aria-hidden>⭐</span>
                      <span>{extended.rating.toFixed(1)}</span>
                      {extended.userRatingCount !== null && (
                        <span>· {extended.userRatingCount.toLocaleString()} reviews</span>
                      )}
                    </p>
                  )}

                  {/* 2. Opening hours */}
                  {extended.regularOpeningHours && (
                    <div>
                      <button
                        onClick={() => setHoursExpanded((x) => !x)}
                        className="flex items-center gap-1.5 text-[14px] font-medium"
                      >
                        {(() => {
                          const oh = extended.regularOpeningHours!;
                          const openNow = oh.openNow;
                          const todayIdx = new Date().getDay(); // 0=Sun..6=Sat
                          // Google weekdayDescriptions[0] = Monday in en-GB style.
                          // Map JS Sunday(0)->index 6, Mon(1)->0, ... Sat(6)->5.
                          const mapped = todayIdx === 0 ? 6 : todayIdx - 1;
                          const todayLine = oh.weekdayDescriptions?.[mapped];
                          const todayHours = todayLine?.split(": ").slice(1).join(": ") ?? "";
                          return (
                            <>
                              <span
                                style={{
                                  color: openNow
                                    ? "var(--colour-success)"
                                    : "var(--colour-danger, #d33)",
                                }}
                              >
                                {openNow ? "Open" : "Closed"}
                              </span>
                              {openNow && todayHours && (
                                <span className="text-[var(--colour-text-secondary)] font-normal">
                                  · {todayHours}
                                </span>
                              )}
                              {hoursExpanded ? (
                                <ChevronUp size={14} className="text-[var(--colour-text-secondary)]" />
                              ) : (
                                <ChevronDown size={14} className="text-[var(--colour-text-secondary)]" />
                              )}
                            </>
                          );
                        })()}
                      </button>
                      {hoursExpanded && extended.regularOpeningHours.weekdayDescriptions && (
                        <ul className="mt-2 space-y-1 text-[13px] text-[var(--colour-text-secondary)]">
                          {extended.regularOpeningHours.weekdayDescriptions.map((d, i) => (
                            <li key={i}>{d}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* 3. Phone */}
                  {(extended.nationalPhoneNumber || extended.internationalPhoneNumber) && (
                    <a
                      href={`tel:${(extended.nationalPhoneNumber || extended.internationalPhoneNumber)!.replace(/\s+/g, "")}`}
                      className="flex items-center gap-2 text-[14px]"
                      style={{ color: "var(--colour-text-primary)" }}
                    >
                      <Phone size={16} />
                      <span>
                        {extended.nationalPhoneNumber ?? extended.internationalPhoneNumber}
                      </span>
                    </a>
                  )}

                  {/* 4. Website */}
                  {extended.websiteUri && (
                    <a
                      href={extended.websiteUri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-[14px]"
                      style={{ color: "var(--colour-text-primary)" }}
                    >
                      <Globe size={16} />
                      <span>Visit website</span>
                    </a>
                  )}

                  {/* 5. Book a table */}
                  {extended.websiteUri && (
                    <a
                      href={extended.websiteUri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-[var(--radius-md)] text-[14px] font-semibold"
                      style={{
                        backgroundColor: "#f97316",
                        color: "white",
                      }}
                    >
                      <Calendar size={16} />
                      Book a table
                    </a>
                  )}

                  {/* 6. Google reviews */}
                  {extended.reviews.length > 0 && (
                    <div className="pt-1">
                      <h3 className="text-[14px] font-semibold mb-2">Reviews</h3>
                      <ul className="space-y-3">
                        {extended.reviews.slice(0, 3).map((rev, i) => {
                          const text = rev.text?.text ?? rev.originalText?.text ?? "";
                          const expanded = !!expandedReviews[i];
                          return (
                            <li
                              key={i}
                              className="p-3 rounded-[var(--radius-md)]"
                              style={{ backgroundColor: "var(--colour-surface-raised)" }}
                            >
                              <div className="flex items-center gap-2 text-[12px] text-[var(--colour-text-secondary)]">
                                <span className="flex items-center gap-0.5">
                                  {Array.from({ length: 5 }).map((_, s) => (
                                    <Star
                                      key={s}
                                      size={12}
                                      fill={s < Math.round(rev.rating ?? 0) ? "currentColor" : "none"}
                                      stroke="currentColor"
                                    />
                                  ))}
                                </span>
                                {rev.relativePublishTimeDescription && (
                                  <span>· {rev.relativePublishTimeDescription}</span>
                                )}
                              </div>
                              {text && (
                                <>
                                  <p
                                    className={`mt-1.5 text-[13px] ${expanded ? "" : "line-clamp-3"}`}
                                  >
                                    {text}
                                  </p>
                                  {text.length > 120 && (
                                    <button
                                      onClick={() =>
                                        setExpandedReviews((m) => ({ ...m, [i]: !expanded }))
                                      }
                                      className="mt-1 text-[12px] font-medium text-[var(--colour-text-secondary)]"
                                    >
                                      {expanded ? "Show less" : "Read more"}
                                    </button>
                                  )}
                                </>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                      <p className="mt-2 text-[11px] text-[var(--colour-text-secondary)]">
                        Reviews from Google
                      </p>
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </div>

        {/* CTA */}
        {r && (
          <div
            className="px-4 pt-3 pb-4 border-t shrink-0"
            style={{ borderColor: "var(--colour-border)" }}
          >
            {visitedToday ? (
              <FButton fullWidth variant="outline" disabled>
                Visited today ✓
              </FButton>
            ) : (
              <FButton fullWidth onClick={handleLog}>
                Log a visit here
              </FButton>
            )}
          </div>
        )}
      </div>

      {suggestOpen && (
        <div className="absolute inset-0 z-10 flex items-end">
          <button
            aria-label="Close suggestions"
            onClick={() => setSuggestOpen(false)}
            className="absolute inset-0 bg-black/50 animate-in fade-in duration-200"
          />
          <div
            className="relative w-full max-h-[70vh] flex flex-col animate-in slide-in-from-bottom duration-300"
            style={{
              backgroundColor: "var(--colour-surface)",
              borderTopLeftRadius: "var(--radius-lg)",
              borderTopRightRadius: "var(--radius-lg)",
              paddingBottom: "env(safe-area-inset-bottom)",
            }}
          >
            <div className="pt-2.5 pb-1 flex justify-center shrink-0">
              <div
                className="w-10 h-1 rounded-full"
                style={{ backgroundColor: "var(--colour-border)" }}
              />
            </div>
            <div className="px-4 pt-2 pb-3 shrink-0">
              <h3 className="text-[18px] font-bold">What cuisine is this?</h3>
              <p className="text-[13px] text-[var(--colour-text-secondary)] mt-0.5">
                Tap one to help others find it.
              </p>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              <div className="flex flex-wrap gap-2">
                {leafCuisines.map((c) => (
                  <button
                    key={c.id}
                    disabled={!!suggesting}
                    onClick={() => handleSuggest(c)}
                    className="px-3 py-2 rounded-[var(--radius-full)] text-[13px] font-medium disabled:opacity-50"
                    style={{
                      backgroundColor:
                        suggesting === c.id
                          ? "var(--colour-accent)"
                          : "var(--colour-surface-raised)",
                      color: "var(--colour-text-primary)",
                    }}
                  >
                    {c.emoji ?? ""} {c.name}
                  </button>
                ))}
                {leafCuisines.length === 0 && (
                  <p className="text-[13px] text-[var(--colour-text-secondary)]">
                    Loading…
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
